interface FilterOption {
  id: string;
  buttonId: string;
  label: string;
  className: string;
}

enum themeAction {
  toggleMenu = "toggleThemeMenu",
  themeClicked = "themeClicked",
  themeRollover = "themeRollover",
  themeRollout = "themeRollout",
}

interface FilterState {
  isFilterMenuOpen: boolean;
  selectedFilterId: string;
}

interface IThemesCallback {
  ACTION: themeAction;
  state: FilterState;
}

let state: FilterState = {
  isFilterMenuOpen: false,
  selectedFilterId: "all",
};
let gPreviousItem: HTMLElement;
let gFilterMenuItems: FilterOption[];
let gThemesCallback: Function;
let gIsSmallBreakpoint: boolean = true;
const LARGER_BREAKPOINT: number = 700;

const setIsMenuDisplayed = (
  isFilterMenuOpen: boolean,
  themesCallback: Function
) => {
  renderFilterMenu(isFilterMenuOpen);
  themesCallback({
    ACTION: themeAction.toggleMenu,
    state: { isFilterMenuOpen: isFilterMenuOpen },
  });
};

const filterButtonEvent = (
  buttonAction: themeAction,
  buttonEl: HTMLElement
) => {
  state.selectedFilterId = buttonEl.dataset.id!;
  gThemesCallback({
    ACTION: buttonAction,
    state,
  });
};

const handleClick = (event: Event) => {
  const buttonEl = event.currentTarget as HTMLElement;
  renderFilterItemDeselected(gPreviousItem);
  renderFilterItemSelected(buttonEl);
  gPreviousItem = buttonEl;
  filterButtonEvent(themeAction.themeClicked, buttonEl);
  event.stopPropagation();
};

/* rollover events are for larger breakpoint only */
const handleRollover = (event: Event) => {
  const buttonEl: HTMLElement = event.currentTarget as HTMLElement;
  renderFilterItemSelected(buttonEl);
  filterButtonEvent(themeAction.themeRollover, buttonEl);
  event.stopPropagation();
};

const handleRollout = (event: Event) => {
  const buttonEl: HTMLElement = event.currentTarget as HTMLElement;
  renderFilterItemDeselected(buttonEl);
  renderFilterItemSelected(gPreviousItem);
  filterButtonEvent(themeAction.themeRollout, buttonEl);
  event.stopPropagation();
};

const rolloverEvents = (action: string) => {
  for (let thisMenuItem of gFilterMenuItems) {
    const keyEl = document.getElementById(thisMenuItem.buttonId)!;
    if (action === "add") {
      keyEl.addEventListener("mouseover", handleRollover);
      keyEl.addEventListener("mouseout", handleRollout);
    } else {
      keyEl.removeEventListener("mouseover", handleRollover);
      keyEl.removeEventListener("mouseout", handleRollout);
    }
  }
};

const addRolloverEventsOnlyForLargebreakpoint = () => {
  const isSmallBreakpoint: boolean =
    window.innerWidth > LARGER_BREAKPOINT ? false : true;
  if (isSmallBreakpoint !== gIsSmallBreakpoint) {
    gIsSmallBreakpoint = isSmallBreakpoint;
    gIsSmallBreakpoint ? rolloverEvents("remove") : rolloverEvents("add");
  }
};

// dynamically build themes menu and add events to buttons with callbacks to pass data back to main app
const init = (
  filterMenuItems: FilterOption[],
  themesCallback: Function
): void => {
  renderFilterMenu(state.isFilterMenuOpen);

  const filterOverlay = document.getElementById(`filterOverlay`)!;
  filterOverlay.addEventListener("click", (event) => {
    state.isFilterMenuOpen = false;
    setIsMenuDisplayed(state.isFilterMenuOpen, themesCallback);
    event.stopPropagation();
  });

  // NB: event.stopPropagation() needed on filter buttons to stop overlay also being clicked
  const filterBtn: HTMLElement = document.getElementById(`filterBtn`)!;
  filterBtn.addEventListener("click", (event) => {
    state.isFilterMenuOpen = !state.isFilterMenuOpen;
    setIsMenuDisplayed(state.isFilterMenuOpen, themesCallback);
    event.stopPropagation();
  });

  const filterCloseButton = document.getElementById(`filterCloseButton`)!;
  filterCloseButton.addEventListener("click", (event) => {
    state.isFilterMenuOpen = false;
    setIsMenuDisplayed(state.isFilterMenuOpen, themesCallback);
    event.stopPropagation();
  });

  // dynamically build filter option list and add click events
  const filterMenu: HTMLElement = document.getElementById("filterList")!;
  for (let thisMenuItem of filterMenuItems) {
    const button: HTMLButtonElement = createFilterItemMarkup(
      thisMenuItem,
      thisMenuItem.id === "all"
    );
    const listItem: HTMLElement = createListItem(button);
    filterMenu.appendChild(listItem);
    const buttonEl = document.getElementById(thisMenuItem.buttonId)!;
    buttonEl.addEventListener("click", handleClick);
  }

  // add rollover events for filter buttons when showing desktop interface
  // remove rollover events for filter buttons when showing mobile interface
  gFilterMenuItems = filterMenuItems;
  gThemesCallback = themesCallback;
  gPreviousItem = document.getElementById(filterMenuItems[0].buttonId)!;
  addRolloverEventsOnlyForLargebreakpoint();
  window.addEventListener("resize", addRolloverEventsOnlyForLargebreakpoint);
};

/* RENDER */
// create markup for themes button
// e.g. <button id="filterSoundsBtn" data-id="sounds" class="filter-button filter-button--sounds filter-button--deselected">SOUNDS</button>
// then wrap in <li> element and attach to <ul>
const createFilterItemMarkup = (item: FilterOption, isSelected: boolean) => {
  const classModifier: string = isSelected ? "selected" : "deselected";
  let button: HTMLButtonElement = document.createElement("button");
  button.setAttribute("id", item.buttonId);
  button.setAttribute("data-id", item.id);
  button.setAttribute(
    "class",
    `filter-button filter-button--${item.id} filter-button--${classModifier}`
  );
  let label = document.createTextNode(item.label);
  button.appendChild(label);
  return button;
};

const createListItem = (children: HTMLButtonElement): HTMLElement => {
  const listItem: HTMLElement = document.createElement("li");
  listItem.appendChild(children);
  return listItem;
};

// This code is simply for changing classnames to update the display of filter elements
// change attributes when toggling themes menu
const renderFilterMenu = (isMenuOpen: boolean) => {
  const filterBtn: HTMLElement = document.getElementById("filterBtn")!;
  const themesBtnChevron: HTMLElement = document.getElementById("themesIcon")!;
  const filterMenu: HTMLElement = document.getElementById("filterList")!;
  const overlay: HTMLElement = document.getElementById("filterOverlay")!;
  const closeBtn: HTMLElement = document.getElementById("filterCloseButton")!;
  if (isMenuOpen) {
    filterBtn.setAttribute("aria-expanded", "true");
    themesBtnChevron.className = "chevron";
    filterMenu.className = `filter-list filter-list--open`;
    closeBtn.classList.add("hide-at-large");
    overlay.classList.remove("filter-screen__overlay--closed");
  } else {
    filterBtn.setAttribute("aria-expanded", "false");
    themesBtnChevron.className = "chevron chevron--down";
    filterMenu.className = `filter-list filter-list--close`;
    closeBtn.classList.remove("hide-at-large");
    overlay.classList.add("filter-screen__overlay--closed");
  }
};

const renderFilterItemSelected = (buttonEl: HTMLElement) => {
  buttonEl.classList.add("filter-button--selected");
  buttonEl.classList.remove("filter-button--deselected");
};

const renderFilterItemDeselected = (buttonEl: HTMLElement) => {
  buttonEl.classList.remove("filter-button--selected");
  buttonEl.classList.add("filter-button--deselected");
};

// TODO: i don't want to have to export rolloverEvents
// this should be set and unset automatically when the window.width changes between 700px breakpoint
// but does not seem to work for testing so having to force using rolloverEvents("add")
export { init, rolloverEvents, IThemesCallback, themeAction, FilterOption };
